import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import binIcon from '../../../assets/bin.svg';

import { Upload, UploadFileInfo, UploadOnAddEvent, UploadOnProgressEvent, UploadOnStatusChangeEvent } from '@progress/kendo-react-upload';
import { useEffect, useMemo, useState } from 'react';
import styles from './ImageViewField.module.scss';
import { Label } from '@progress/kendo-react-labels';
import { notifications } from '../../../services';
import { SecureImgDataUrl } from '../../../components/common';

const allowedExtensions = ['.jpg', '.png', '.svg'];
const maxFileSize = 3 * 1024 * 1024;

interface IProps extends FieldRenderProps {
  edit?: boolean;
}

export const ImageViewField = (fieldRenderProps: IProps) => {
  const { validationMessage, touched, onChange, edit, label, id, valid, hint, disabled, optional, required, className, ...others } =
    fieldRenderProps;

  const [files, setFiles] = useState<Array<UploadFileInfo>>([]);
  const [src, setSrc] = useState<string | undefined>(fieldRenderProps.src);

  useEffect(() => {
    if (files.length > 0) {
      onChange({ value: files[0] });
    }
  }, [files, onChange]);

  const onAdd = (event: UploadOnAddEvent) => {
    if (event.newState.length === 0) return;
    if (event.newState[0].validationErrors && event.newState[0].validationErrors.length > 0) {
      notifications.warning(
        `Image can't be uploaded. File size should be less than 3MB and type should be ${allowedExtensions.join(',')}.`,
      );
    } else {
      setFiles(event.newState);
    }
  };

  const onProgress = (event: UploadOnProgressEvent) => {
    setFiles(event.newState);
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState);
  };

  const onSaveRequest = (
    files: UploadFileInfo[],
    options: { formData: FormData; requestOptions: any },
    onProgress: (uid: string, event: ProgressEvent<EventTarget>) => void,
  ): Promise<{ uid: string }> => {
    const currentFile = files[0] as UploadFileInfo;
    const uid = currentFile.uid;

    return new Promise<{ uid: string }>((resolve, reject) => {
      if (currentFile.validationErrors && currentFile.validationErrors.length > 0) {
        reject({ uid: uid });
      } else {
        const reader = new FileReader();

        reader.onload = () => {
          if (reader.result && typeof reader.result === 'string') {
            setSrc(reader.result);
            // update viewModel and resolve
            resolve({ uid: uid });
          } else {
            reject({ uid: uid });
          }
        };
        // onprogress is fired periodically as the FileReader reads data and the ProgressEvent can be passed directly to the Upload control, handy!
        reader.onprogress = (data) => {
          onProgress(uid, data);
        };
        // if the read is not completed due to error or user intervention, reject
        reader.onabort = () => {
          reject({ uid: uid });
        };
        reader.onerror = () => {
          reject({ uid: uid });
        };

        reader.readAsDataURL(currentFile.getRawFile!());
      }
    });
  };

  const onRemoveRequest = (files: UploadFileInfo[], options: { formData: FormData; requestOptions: any }): Promise<{ uid: string }> => {
    const currentFile = files[0] as UploadFileInfo;
    const uid = currentFile.uid;

    return new Promise<{ uid: string }>((resolve) => {
      resolve({ uid: uid });
    });
  };

  const onImgDelete = () => {
    setSrc(undefined);
    setFiles([]);
    fieldRenderProps.onChange({ value: null });
  };

  const viewContent = useMemo(() => <SecureImgDataUrl className={styles.img} src={src} alt={label} />, [label, src]);

  const editContent = useMemo(
    () => (
      <>
        {!src && (
          <Upload
            accept={allowedExtensions.join(',')}
            restrictions={{
              allowedExtensions: allowedExtensions,
              maxFileSize: maxFileSize,
            }}
            batch={false}
            multiple={false}
            showFileList={false}
            files={files}
            onAdd={onAdd}
            onProgress={onProgress}
            onStatusChange={onStatusChange}
            withCredentials={false}
            saveUrl={onSaveRequest}
            removeUrl={onRemoveRequest}
            className={styles.upload}
            {...others}
          />
        )}

        {!!src && (
          <div style={{ position: 'absolute' }}>
            <button onClick={() => onImgDelete()} className={styles.bin}>
              <img src={binIcon} alt='bin' />
            </button>
            <SecureImgDataUrl className={styles.img} src={src} alt={label} />
          </div>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, label, others, src],
  );

  const content = useMemo(
    () => (
      <FieldWrapper className={className}>
        <Label editorId={id} editorValid={valid} optional={optional} className={`${edit && required ? 'required' : null}`}>
          {label}
        </Label>
        <div className={`k-form-field-wrap ${styles.panel}`}>{edit ? editContent : viewContent}</div>
      </FieldWrapper>
    ),
    [className, edit, editContent, id, label, optional, required, valid, viewContent],
  );

  return content;
};
